import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TableBody,
    TableContainer,
    Table,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Chip,
} from '@mui/material';
import PropTypes from 'prop-types';

export default function SurveyPhraseSummary({ mediaWithPhrases, phraseCategories }) {
    const { t } = useTranslation();

    // Transform and group phrases by category with deduplication
    const groupedPhrases = useMemo(() => {
        if (!phraseCategories || !Array.isArray(phraseCategories)) {
            console.warn('No phrase categories provided, cannot group phrases');
            return [];
        }

        // Flatten all phrases from media with their context
        const allMediaPhrases = mediaWithPhrases.reduce((acc, media) => {
            if (!media.phrases || !Array.isArray(media.phrases)) {
                return acc;
            }

            const phrasesWithContext = media.phrases.map((phrase) => ({
                ...phrase,
                mediaId: media.imageId || media.videoId,
                mediaType: media.mediaType,
                // NEW: Include machine context
                machineRoomName: media.machineRoomName,
                machineName: media.machineName,
                machineType: media.machineType,
            }));

            return acc.concat(phrasesWithContext);
        }, []);

        console.log('Flattened media phrases:', allMediaPhrases);

        if (allMediaPhrases.length === 0) {
            return [];
        }

        // Build a map of phraseId -> category for quick lookups
        const phraseIdToCategoryMap = new Map();

        phraseCategories.forEach((category) => {
            if (category.phrases && Array.isArray(category.phrases)) {
                category.phrases.forEach((phrase) => {
                    phraseIdToCategoryMap.set(phrase.id, {
                        categoryId: category.id,
                        categoryTitle: category.title,
                        phraseData: phrase,
                    });
                });
            }
        });

        // Group media phrases by category with deduplication
        const categoriesWithPhrases = [];
        const categoryMap = new Map();

        allMediaPhrases.forEach((mediaPhrase) => {
            const categoryInfo = phraseIdToCategoryMap.get(mediaPhrase.id);

            if (!categoryInfo) {
                console.warn(`Phrase ${mediaPhrase.id} not found in any category`);
                return;
            }

            const { categoryId, categoryTitle, phraseData } = categoryInfo;

            // Initialize category group if it doesn't exist
            if (!categoryMap.has(categoryId)) {
                const categoryGroup = {
                    id: categoryId,
                    title: categoryTitle,
                    phrases: [],
                    phraseMap: new Map(),
                };
                categoryMap.set(categoryId, categoryGroup);
                categoriesWithPhrases.push(categoryGroup);
            }

            const category = categoryMap.get(categoryId);
            const phraseId = phraseData.id;

            // Check if we've already seen this phrase in this category
            if (category.phraseMap.has(phraseId)) {
                // Add source with machine context
                const existingPhrase = category.phraseMap.get(phraseId);
                existingPhrase.sources.push({
                    mediaId: mediaPhrase.mediaId,
                    mediaType: mediaPhrase.mediaType,
                    machineRoomName: mediaPhrase.machineRoomName,
                    machineName: mediaPhrase.machineName,
                    machineType: mediaPhrase.machineType,
                });
            } else {
                // New phrase for this category
                const deduplicatedPhrase = {
                    id: phraseData.id,
                    observation: phraseData.observation,
                    solution: phraseData.solution,
                    benefits: phraseData.benefits,
                    sources: [
                        {
                            mediaId: mediaPhrase.mediaId,
                            mediaType: mediaPhrase.mediaType,
                            machineRoomName: mediaPhrase.machineRoomName,
                            machineName: mediaPhrase.machineName,
                            machineType: mediaPhrase.machineType,
                        },
                    ],
                };
                category.phraseMap.set(phraseId, deduplicatedPhrase);
                category.phrases.push(deduplicatedPhrase);
            }
        });

        // Clean up temporary phraseMap
        categoriesWithPhrases.forEach((category) => {
            delete category.phraseMap;
        });

        // Sort categories by ID
        categoriesWithPhrases.sort((a, b) => a.id - b.id);

        console.log('Grouped and deduplicated phrases by category:', categoriesWithPhrases);

        return categoriesWithPhrases;
    }, [mediaWithPhrases, phraseCategories]);

    // Helper to render machine sources as chips
    const renderMachineSources = (sources) => {
        // Get unique machine names (in case same phrase appears in multiple media from same machine)
        const uniqueMachines = Array.from(new Map(sources.map((s) => [s.machineName, s])).values());

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {uniqueMachines.map((source, index) => (
                    <Chip
                        key={`${source.machineName}-${index}`}
                        label={source.machineName}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                    />
                ))}
            </Box>
        );
    };

    // Guard clauses
    if (!mediaWithPhrases || mediaWithPhrases.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary">
                {t('survey.noPhrases')}
            </Typography>
        );
    }

    if (groupedPhrases.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary">
                {t('survey.noPhrasesInCategories')}
            </Typography>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('survey.summaryObservations')}
            </Typography>

            <TableContainer component={Paper}>
                <Table size="small" aria-label={t('survey.phraseSummary')}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                {t('phrases.category')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('phrases.observation')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('phrases.solution')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('phrases.benefits')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('phrases.illustratedInReport')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupedPhrases.map((category) =>
                            category.phrases.map((phrase, phraseIndex) => (
                                <TableRow key={`phrase-${phrase.id}`} hover>
                                    {phraseIndex === 0 && (
                                        <TableCell
                                            component="th"
                                            scope="rowgroup"
                                            rowSpan={category.phrases.length}
                                            sx={{
                                                fontWeight: 'bold',
                                                backgroundColor: '#fafafa',
                                                verticalAlign: 'top',
                                                borderRight: '2px solid #e0e0e0',
                                            }}>
                                            {category.title}
                                        </TableCell>
                                    )}
                                    <TableCell>{phrase.observation || '-'}</TableCell>
                                    <TableCell>{phrase.solution || '-'}</TableCell>
                                    <TableCell>{phrase.benefits || '-'}</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>{renderMachineSources(phrase.sources)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

SurveyPhraseSummary.propTypes = {
    mediaWithPhrases: PropTypes.arrayOf(
        PropTypes.shape({
            mediaType: PropTypes.string,
            imageId: PropTypes.string,
            videoId: PropTypes.string,
            machineRoomName: PropTypes.string,
            machineName: PropTypes.string,
            machineType: PropTypes.number,
            phrases: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    observation: PropTypes.string,
                    solution: PropTypes.string,
                    benefits: PropTypes.string,
                })
            ),
        })
    ).isRequired,
    phraseCategories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            title: PropTypes.string.isRequired,
            phrases: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    observation: PropTypes.string.isRequired,
                    solution: PropTypes.string.isRequired,
                    benefits: PropTypes.string.isRequired,
                })
            ),
        })
    ),
};

SurveyPhraseSummary.defaultProps = {
    phraseCategories: null,
};
